import React from 'react';

interface ButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: 'primary' | 'secondary' | 'ghost' | 'outline';
  size?: 'sm' | 'md' | 'lg';
  asLink?: boolean;
  href?: string;
  isLoading?: boolean;
  leftIcon?: React.ReactNode;
  rightIcon?: React.ReactNode;
}

const Button: React.FC<ButtonProps> = ({
  variant = 'primary',
  size = 'md',
  asLink = false,
  href,
  isLoading = false,
  leftIcon,
  rightIcon,
  children,
  className = '',
  disabled,
  ...props
}) => {
  const baseClasses = 
    'group inline-flex items-center justify-center font-bold tracking-wider uppercase transition-all duration-300 focus-ring disabled:pointer-events-none disabled:opacity-50 relative overflow-hidden';

  const sizeClasses = {
    sm: 'text-xs px-4 py-2',
    md: 'text-sm px-6 py-3',
    lg: 'text-base px-8 py-4',
  };

  const variantClasses = {
    primary: 'bg-foreground border-2 border-foreground text-background hover:bg-primary hover:text-primary-foreground hover:border-primary',
    secondary: 'bg-transparent border-2 border-current text-foreground hover:bg-foreground hover:text-background',
    ghost: 'border-2 border-transparent hover:bg-secondary hover:border-secondary',
    outline: 'bg-transparent border-2 border-primary text-primary hover:bg-primary hover:text-primary-foreground',
  };

  const combinedClasses = `${baseClasses} ${sizeClasses[size]} ${variantClasses[variant]} ${className}`;

  const content = (
    <>
      {isLoading && (
        <span className="absolute inset-0 flex items-center justify-center">
          <span className="w-4 h-4 border-2 border-current border-t-transparent rounded-full animate-spin" />
        </span>
      )}
      <span className={`flex items-center ${isLoading ? 'invisible' : ''}`}>
        {leftIcon && <span className="mr-2">{leftIcon}</span>}
        {children}
        {rightIcon && <span className="ml-2 transition-transform duration-300 ease-out group-hover:rotate-90">{rightIcon}</span>}
      </span>
    </>
  );

  if (asLink && href) {
    return (
      <a 
        href={href} 
        className={combinedClasses}
        aria-disabled={disabled || isLoading}
      >
        {content}
      </a>
    );
  }

  return (
    <button 
      className={combinedClasses} 
      disabled={disabled || isLoading}
      {...props}
    >
      {content}
    </button>
  );
};

export default Button;
